/* OVREL motion — loaded on every page (deferred). No dependencies.
   1. Smooth scrolling for notched mouse wheels. Trackpads and touch keep the
      system's own scrolling (it already has momentum; easing on top of it made
      a trackpad trail the finger by up to 90px). Nothing runs when the visitor
      prefers reduced motion.
   2. Gentle reveal of blocks as they scroll into view.
   3. A slow focus pull across the homepage mosaic (v37).
   4. Page transitions: a short fade to black between pages of the site (v37).
   Both are progressive: if this file fails, the page scrolls and shows as normal. */
(function () {
  'use strict';
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduce.matches) return;
  var root = document.documentElement;

  /* ------------------------------------------------------ smooth wheel */
  function smoothWheel() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    var EASE = 0.16;           // share of the remaining distance covered per 60 Hz frame (settles in ~400ms)
    var target = 0, current = 0, written = -1, raf = 0, last = 0;

    function limit() { return Math.max(0, root.scrollHeight - window.innerHeight); }
    function locked() { return document.body.classList.contains('ov-lock') || !!document.querySelector('dialog[open]'); }
    // a wheel over something that scrolls on its own (drawer, menu, filter list) belongs to that element
    function ownScroller(el, dy) {
      for (; el && el !== document.body && el !== root; el = el.parentElement) {
        if (el.nodeType !== 1) continue;
        if (el.hasAttribute('data-ov-native-scroll')) return true;
        var oy = getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
          if (dy > 0 ? el.scrollTop + el.clientHeight < el.scrollHeight - 1 : el.scrollTop > 0) return true;
        }
      }
      return false;
    }
    function stop() { if (raf) cancelAnimationFrame(raf); raf = 0; written = -1; }
    function tick(now) {
      // someone else moved the page (keyboard, scrollbar, anchor, script): hand control back
      if (written >= 0 && Math.abs(window.scrollY - written) > 3) { stop(); return; }
      var dt = Math.min(64, now - last); last = now;
      current += (target - current) * (1 - Math.pow(1 - EASE, dt / 16.667));
      if (Math.abs(target - current) < 0.4) current = target;
      window.scrollTo(0, current);
      written = window.scrollY;
      raf = current === target ? 0 : requestAnimationFrame(tick);
      if (!raf) written = -1;
    }
    window.addEventListener('wheel', function (e) {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || locked()) return;
      var dy = e.deltaY;
      // Mouse wheel or trackpad? Chrome and Safari report a trackpad's legacy wheelDeltaY as
      // exactly -3 x deltaY; a mouse wheel reports notches of 120. Firefox has no wheelDeltaY
      // and sends lines (deltaMode 1) for a mouse. Trackpads keep the system's own momentum.
      var trackpad = typeof e.wheelDeltaY === 'number' && e.wheelDeltaY !== 0
        ? e.wheelDeltaY === -3 * e.deltaY
        : e.deltaMode === 0;
      if (trackpad || e.deltaX !== 0) { if (raf) stop(); return; }
      if (e.deltaMode === 1) dy *= 16; else if (e.deltaMode === 2) dy *= window.innerHeight;
      if (Math.abs(e.deltaX) > Math.abs(dy) || dy === 0) return;
      if (ownScroller(e.target, dy)) return;
      e.preventDefault();
      if (!raf) { current = target = window.scrollY; last = performance.now(); }
      target = Math.max(0, Math.min(limit(), target + dy));
      if (!raf) raf = requestAnimationFrame(tick);
    }, { passive: false });
  }

  /* ------------------------------------------------------------ reveal */
  var SELECTOR = '.ov-head, .ov-card, .ov-tile, .ov-pair, .ov-framing__grid, .ov-edroom__media, .ov-story, .ov-news';
  function reveal() {
    var main = document.getElementById('main');
    if (!main || !('IntersectionObserver' in window)) return;
    var queue = [], pending = 0;
    function flush() {
      pending = 0;
      queue.sort(function (a, b) {
        var ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect();
        return Math.round(ra.top - rb.top) || ra.left - rb.left;
      });
      queue.forEach(function (el, i) {
        var delay = Math.min(i, 3) * 50;
        el.style.setProperty('--rv-delay', delay + 'ms');
        el.classList.add('is-in');
        // hand the element back to its own styles once it has arrived
        setTimeout(function () { el.classList.remove('ov-reveal', 'is-in'); el.style.removeProperty('--rv-delay'); }, 760 + delay);
      });
      queue = [];
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        queue.push(en.target);
      });
      if (queue.length && !pending) pending = requestAnimationFrame(flush);
    }, { rootMargin: '0px 0px -6% 0px' });
    var vh = window.innerHeight, vw = window.innerWidth;
    Array.prototype.forEach.call(main.querySelectorAll(SELECTOR), function (el) {
      if (el.parentElement.closest('.ov-reveal')) return;
      var r = el.getBoundingClientRect();
      // only blocks that start below the fold: nothing already on screen ever blinks, and off-canvas rail items stay put
      if (r.top < vh || r.width === 0 || r.left >= vw || r.right <= 0) return;
      el.classList.add('ov-reveal');
      io.observe(el);
    });
  }

  /* ------------------------------------------------------ mosaic focus */
  // One tile at a time comes slowly into focus and drifts back out, like a lens
  // pulling across the wall. Only while the hero is on screen and the tab is visible.
  function mosaicFocus() {
    var hero = document.querySelector('[data-ov-mosaic]');
    if (!hero) return;
    var tiles = Array.prototype.slice.call(hero.querySelectorAll('.ov-mosaic__tile'));
    var onScreen = true, timer = 0, last = null;
    function step() {
      timer = 0;
      if (last) last.classList.remove('is-focus');
      if (!onScreen || document.hidden) return;
      var shown = tiles.filter(function (t) { return t !== last && t.offsetParent !== null; });
      if (!shown.length) return;
      last = shown[Math.floor(Math.random() * shown.length)];
      last.classList.add('is-focus');
      timer = setTimeout(step, 3400);
    }
    function wake() { if (!timer && onScreen && !document.hidden) timer = setTimeout(step, 600); }
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (en) { onScreen = en[0].isIntersecting; if (onScreen) wake(); }, { threshold: 0.15 }).observe(hero);
    }
    document.addEventListener('visibilitychange', wake);
    timer = setTimeout(step, 2600);
  }

  /* ------------------------------------------------- page transitions */
  // A same-site link fades the page to black for 280ms, then leaves; the next page's <head>
  // sees ov:nav and lifts the curtain (CSS in ov.css). The next page is usually prefetched
  // already (speculation rules in theme.liquid), so the cover costs about what it shows.
  var SKIP_PATH = /^\/(cart|checkout|checkouts|account|password|challenge|apps|services|tools)(\/|$)/;
  function transitions() {
    if (window.Shopify && window.Shopify.designMode) return;
    document.addEventListener('click', function (e) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var a = e.target.closest ? e.target.closest('a[href]') : null;
      if (!a || a.hasAttribute('download') || a.hasAttribute('data-ov-open') || a.hasAttribute('data-ov-no-transition')) return;
      if (a.target && a.target !== '_self') return;
      var url;
      try { url = new URL(a.getAttribute('href'), window.location.href); } catch (err) { return; }
      if (url.origin !== window.location.origin || SKIP_PATH.test(url.pathname)) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search) return; // same page or #anchor
      e.preventDefault();
      try { window.sessionStorage.setItem('ov:nav', String(Date.now())); } catch (err) {}
      var dlg = a.closest('dialog[open]');
      if (dlg && typeof dlg.close === 'function') dlg.close(); // a dialog sits in the top layer, above any curtain
      root.classList.add('ov-leaving');
      setTimeout(function () { window.location.href = url.href; }, 280);
      // if the browser never leaves (a download, a 204), give the page back
      setTimeout(function () { root.classList.remove('ov-leaving'); }, 4000);
    });
    // back/forward cache restores the page exactly as it was left: covered
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      root.classList.remove('ov-leaving', 'ov-arrive', 'ov-intro');
      try { window.sessionStorage.removeItem('ov:nav'); } catch (err) {}
    });
  }

  smoothWheel();
  transitions();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { reveal(); mosaicFocus(); });
  else { reveal(); mosaicFocus(); }
})();
